import React, { useCallback, useState, useEffect } from 'react';
import api, { logout } from '../api';
import { useFeature } from '../hooks/useFeature';
import FinanceSettings from '../components/FinanceSettings';
import SignaturePad from '../components/SignaturePad';
import BankConnections from '../components/BankConnections';
import { parseChain, formatChain, levelOptions, levelColor, levelFromRole, roleForLevel, roleLabel as levelRoleLabel, MAX_LEVEL } from '../lib/levels';

const API = '/api';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  created_at: string;
  last_login?: string | null;
  slack_user_id?: string;
  telegram_chat_id?: string;
  notify_summary?: boolean;
  notify_new_document?: boolean;
  notify_issue?: boolean;
  notify_via_email?: boolean;
  notify_via_slack?: boolean;
  notify_via_telegram?: boolean;
  notify_via_push?: boolean;
}

interface Rule {
  id: string;
  name: string;
  amount_min: number;
  amount_max: number;
  category: string;
  required_level: string;
}

interface Category {
  id: string;
  name: string;
  keywords: string | null;
  created_at: string;
}

interface ArchiveMonth { month: string; total: number; }

// Výber úrovní pravidla — prepínacie štítky L1..LN podľa toho, koľko úrovní
// firma používa. Aspoň jedna úroveň musí ostať zapnutá.
function LevelPicker({ value, onChange, levelCount }: { value: string; onChange: (v: string) => void; levelCount: number }) {
  const active = parseChain(value);
  const toggle = (level: number) => {
    const next = active.includes(level) ? active.filter(n => n !== level) : [...active, level];
    if (next.length === 0) return;
    onChange(formatChain(next));
  };
  const tagStyle = (active: boolean, color: string) => ({
    padding: '0.3rem 0.85rem',
    borderRadius: '999px',
    border: `2px solid ${active ? color : '#d1d5db'}`,
    backgroundColor: active ? color : 'white',
    color: active ? 'white' : '#6b7280',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
    fontSize: '0.875rem',
    transition: 'all 0.15s',
    userSelect: 'none' as const,
  });
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
      {levelOptions(levelCount).map(n => (
        <span key={n} onClick={() => toggle(n)} style={tagStyle(active.includes(n), levelColor(n))}>L{n}</span>
      ))}
    </div>
  );
}

export default function Admin() {
  const [activeTab, setActiveTab] = useState('users');
  const [bankMsg, setBankMsg] = useState<string | null>(null);

  // Návrat zo SCA autorizácie banky (redirect z /api/invoicing/bank/callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('bank_connected') || params.has('bank_error')) {
      const err = params.get('bank_error');
      setActiveTab('bank');
      setBankMsg(err ? `Pripojenie banky zlyhalo: ${err}` : 'Banka úspešne pripojená.');
      window.history.replaceState(null, '', window.location.pathname);
    }
    // Priamy odkaz na záložku (napr. z modulu Financie: /admin?tab=finance)
    // (napr. z modulu Financie: ?tab=finance, z pushu o výpadku banky: ?tab=bank)
    const tabParam = params.get('tab');
    if (tabParam === 'finance' || tabParam === 'bank') setActiveTab(tabParam);
  }, []);
  // Podpis firmy — nastavenie naprieč modulmi (dnes ho tlačí fakturácia na FA)
  const [showSignature, setShowSignature] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);

  // Náhľad uloženého podpisu. Blob URL uvoľňujeme pri výmene aj odchode zo
  // stránky, nech neostávajú visieť v pamäti.
  const loadSignature = useCallback(() => {
    api.get('/settings/signature', { responseType: 'blob' })
      .then(r => setSignatureUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); }))
      .catch(() => setSignatureUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }));
  }, []);

  useEffect(() => { loadSignature(); }, [loadSignature]);
  useEffect(() => () => { if (signatureUrl) URL.revokeObjectURL(signatureUrl); }, [signatureUrl]);
  const [users, setUsers] = useState<User[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryKeywords, setNewCategoryKeywords] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryKeywords, setEditCategoryKeywords] = useState('');
  const [archiveMonths, setArchiveMonths] = useState<ArchiveMonth[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [downloadingMonth, setDownloadingMonth] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', role: '', password: '', slack_user_id: '', telegram_chat_id: '', notify_summary: true, notify_new_document: true, notify_issue: true, notify_via_email: false, notify_via_slack: true, notify_via_telegram: false, notify_via_push: true });
  const [editSaving, setEditSaving] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [editRuleForm, setEditRuleForm] = useState({ name: '', amountMin: 0, amountMax: '', category: '', requiredLevel: 'l1', autoApprove: false, textPattern: '', vendorPattern: '' });
  const [editRuleSaving, setEditRuleSaving] = useState(false);
  // Pravidlá sú per modul: došlé doklady (default) vs objednávky (modul Objednávky)
  const [rulesModule, setRulesModule] = useState<'documents' | 'orders'>('documents');
  // Koľko schvaľovacích úrovní firma používa (1–8). Riadi len ponuku v UI —
  // rozbehnuté doklady bežia ďalej podľa reťazca, s ktorým začali.
  const [levelCount, setLevelCount] = useState(2);
  const [levelCountSaving, setLevelCountSaving] = useState(false);
  const hasOrdersFeature = useFeature('orders');
  const hasFinanceFeature = useFeature('finance_overview');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [newUser, setNewUser] = useState({ first_name: '', last_name: '', email: '', role: 'approver_l1', password: '' });
  const [newRule, setNewRule] = useState({ name: '', amountMin: 0, amountMax: '', category: '', requiredLevel: 'l1', autoApprove: false, textPattern: '', vendorPattern: '' });

  const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]).{8,}$/;

  const generatePassword = () => {
    const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const specials = '!@#$%^&*()-_=+';
    const all = letters + digits + specials;
    let pwd = '';
    pwd += letters[Math.floor(Math.random() * letters.length)];
    pwd += digits[Math.floor(Math.random() * digits.length)];
    pwd += specials[Math.floor(Math.random() * specials.length)];
    for (let i = 0; i < 9; i++) pwd += all[Math.floor(Math.random() * all.length)];
    return pwd.split('').sort(() => Math.random() - 0.5).join('');
  };

  useEffect(() => {
    loadUsers();
    loadRules();
    loadArchiveMonths();
    loadCategories();
    loadLevelCount();
  }, []);

  const loadLevelCount = async () => {
    try {
      const res = await api.get('/settings/approval-levels');
      setLevelCount(res.data.levels || 2);
    } catch (err) {
      console.error('Chyba pri načítaní počtu úrovní');
    }
  };

  const saveLevelCount = async (levels: number) => {
    setLevelCountSaving(true);
    try {
      await api.put('/settings/approval-levels', { levels });
      setLevelCount(levels);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    } finally {
      setLevelCountSaving(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch (err) {
      console.error('Chyba pri načítaní kategórií');
    }
  };

  const addCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    setCategoryLoading(true);
    try {
      await api.post('/categories', { name: newCategory.trim(), keywords: newCategoryKeywords.trim() || null });
      setNewCategory('');
      setNewCategoryKeywords('');
      loadCategories();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    } finally {
      setCategoryLoading(false);
    }
  };

  const deleteCategory = async (id: string) => {
    if (!window.confirm('Naozaj vymazať kategóriu?')) return;
    try {
      await api.delete(`/categories/${id}`);
      loadCategories();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  const saveEditCategory = async (id: string) => {
    if (!editCategoryName.trim()) return;
    try {
      await api.put(`/categories/${id}`, { name: editCategoryName.trim(), keywords: editCategoryKeywords.trim() || null });
      setEditingCategory(null);
      loadCategories();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  const loadArchiveMonths = async () => {
    setArchiveLoading(true);
    try {
      const res = await api.get('/documents/archive/months');
      setArchiveMonths(res.data);
    } catch {}
    finally { setArchiveLoading(false); }
  };

  const downloadArchive = async (month: string) => {
    setDownloadingMonth(month);
    try {
      const [year, m] = month.split('-');
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/documents/archive/${year}/${m}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { alert('Chyba pri sťahovaní'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `faktury-${month}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Chyba pri sťahovaní'); }
    finally { setDownloadingMonth(null); }
  };

  const formatMonth = (yyyyMM: string) => {
    const [y, m] = yyyyMM.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString('sk-SK', { month: 'long', year: 'numeric' });
  };

  const loadUsers = async () => {
    try {
      const response = await api.get(`/users`);
      setUsers(response.data);
    } catch (err) {
      console.error('Chyba pri načítaní používateľov');
    } finally {
      setLoading(false);
    }
  };

  const loadRules = async (module?: 'documents' | 'orders') => {
    try {
      const response = await api.get(`/rules`, { params: { module: module || rulesModule } });
      setRules(response.data);
    } catch (err) {
      console.error('Chyba pri načítaní pravidiel');
    }
  };

  // Prepnutie modulu pravidiel (doklady vs objednávky) → načítaj jeho pravidlá
  useEffect(() => {
    loadRules(rulesModule);
    setEditingRule(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesModule]);


  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordRegex.test(newUser.password)) {
      alert('Heslo musí mať min. 8 znakov, písmeno, číslo a špeciálny znak.');
      return;
    }
    try {
      await api.post(`/users`, newUser);
      setNewUser({ first_name: '', last_name: '', email: '', role: 'approver_l1', password: '' });
      loadUsers();
      alert('Používateľ vytvorený');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  const deleteUser = async (id: string) => {
    if (!window.confirm('Naozaj vymazať používateľa?')) return;
    try {
      await api.delete(`/users/${id}`);
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  const startEdit = (u: User) => {
    setEditingUser(u);
    setEditForm({
      first_name: u.first_name || '', last_name: u.last_name || '', email: u.email, role: u.role, password: '',
      slack_user_id: u.slack_user_id || '', telegram_chat_id: u.telegram_chat_id || '',
      notify_summary: u.notify_summary ?? true, notify_new_document: u.notify_new_document ?? true, notify_issue: u.notify_issue ?? true,
      notify_via_email: u.notify_via_email ?? false, notify_via_slack: u.notify_via_slack ?? true, notify_via_telegram: u.notify_via_telegram ?? false, notify_via_push: u.notify_via_push ?? true,
    });
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({ first_name: '', last_name: '', email: '', role: '', password: '', slack_user_id: '', telegram_chat_id: '', notify_summary: true, notify_new_document: true, notify_issue: true, notify_via_email: false, notify_via_slack: true, notify_via_telegram: false, notify_via_push: true });
  };

  const saveEdit = async () => {
    if (!editingUser) return;
    if (editForm.password && !passwordRegex.test(editForm.password)) {
      alert('Heslo musí mať min. 8 znakov, písmeno, číslo a špeciálny znak.');
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/users/${editingUser.id}`, editForm);
      setEditingUser(null);
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri ukladaní');
    } finally {
      setEditSaving(false);
    }
  };

  const startEditRule = (r: Rule) => {
    setEditingRule(r);
    setEditRuleForm({
      name: r.name,
      amountMin: r.amount_min,
      amountMax: r.amount_max ? String(r.amount_max) : '',
      category: r.category || '',
      requiredLevel: r.required_level || 'l1',
      autoApprove: (r as any).auto_approve === true,
      textPattern: (r as any).text_pattern || '',
      vendorPattern: (r as any).vendor_pattern || '',
    });
  };

  const cancelEditRule = () => {
    setEditingRule(null);
  };

  const saveEditRule = async () => {
    if (!editingRule) return;
    setEditRuleSaving(true);
    try {
      await api.put(`/rules/${editingRule.id}`, editRuleForm);
      setEditingRule(null);
      loadRules();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri ukladaní');
    } finally {
      setEditRuleSaving(false);
    }
  };

  const addRule = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Objednávky nemajú kategórie ani auto-schvaľovanie
      const payload = rulesModule === 'orders'
        ? { ...newRule, module: 'orders', category: '', autoApprove: false, textPattern: '', vendorPattern: '' }
        : { ...newRule, module: 'documents' };
      await api.post(`/rules`, payload);
      setNewRule({ name: '', amountMin: 0, amountMax: '', category: '', requiredLevel: 'l1', autoApprove: false, textPattern: '', vendorPattern: '' });
      loadRules();
      alert('Pravidlo vytvorené');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  const deleteRule = async (id: string) => {
    if (!window.confirm('Naozaj vymazať pravidlo?')) return;
    try {
      await api.delete(`/rules/${id}`);
      loadRules();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    }
  };

  const roleLabel = (role: string) => {
    const lvl = levelFromRole(role);
    if (lvl !== null) return levelRoleLabel(lvl);
    switch (role) {
      case 'admin': return 'Admin';
      case 'accountant': return 'Účtovník';
      case 'submitter': return 'Zberač dokladov';
      case 'viewer': return 'Pozorovateľ';
      default: return role;
    }
  };

  const tabStyle = (tab: string) => ({
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
    backgroundColor: 'transparent',
    color: activeTab === tab ? '#2563eb' : '#6b7280',
    fontWeight: activeTab === tab ? 600 : 400,
    cursor: 'pointer',
    fontSize: '1rem'
  });

  const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const
  };

  const inputSmallStyle = {
    ...inputStyle,
    fontSize: '0.875rem',
    padding: '0.375rem 0.5rem',
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', padding: '1rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: '#1f2937', margin: 0 }}>Admin panel</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button onClick={() => window.location.href = '/home'} style={{ padding: '0.5rem 1rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Moduly
          </button>
          <button onClick={logout} style={{ padding: '0.5rem 1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Odhlásiť
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '0 2rem' }}>
        <button style={tabStyle('users')} onClick={() => setActiveTab('users')}>Používatelia</button>
        <button style={tabStyle('rules')} onClick={() => setActiveTab('rules')}>Pravidlá</button>
        <button style={tabStyle('categories')} onClick={() => setActiveTab('categories')}>Kategórie</button>
        <button style={tabStyle('archive')} onClick={() => setActiveTab('archive')}>Archív</button>
        <button style={tabStyle('signature')} onClick={() => setActiveTab('signature')}>Podpis</button>
        <button style={tabStyle('bank')} onClick={() => setActiveTab('bank')}>Banka</button>
        {hasFinanceFeature && <button style={tabStyle('finance')} onClick={() => setActiveTab('finance')}>Financie</button>}
      </div>

      <div style={{ padding: '2rem' }}>

        {/* POUŽÍVATELIA */}
        {activeTab === 'users' && (
          <div>
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginTop: 0, color: '#1f2937' }}>Pridať používateľa</h2>
              <form onSubmit={addUser} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Meno</label>
                  <input style={inputStyle} value={newUser.first_name} onChange={e => setNewUser({...newUser, first_name: e.target.value})} required />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Priezvisko</label>
                  <input style={inputStyle} value={newUser.last_name} onChange={e => setNewUser({...newUser, last_name: e.target.value})} required />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Email</label>
                  <input style={inputStyle} type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} required />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Heslo <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>(min. 8 znakov, písmeno, číslo, špeciálny znak)</span></label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input style={{ ...inputStyle, flex: 1 }} type="text" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required />
                    <button type="button" onClick={() => setNewUser({...newUser, password: generatePassword()})}
                      style={{ padding: '0.4rem 0.75rem', backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      Vygenerovať
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Rola</label>
                  <select style={inputStyle} value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}>
                    {levelOptions(levelCount).map(n => (
                      <option key={n} value={roleForLevel(n)}>{levelRoleLabel(n)}</option>
                    ))}
                    <option value="accountant">Účtovník</option>
                    <option value="admin">Admin</option>
                    <option value="submitter">Zberač dokladov (iba nahráva)</option>
                    <option value="viewer">Pozorovateľ</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <button type="submit" style={{ padding: '0.75rem 2rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem' }}>
                    Pridať používateľa
                  </button>
                </div>
              </form>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Meno</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Email</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Rola</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Posledné prihlásenie</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Akcia</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <React.Fragment key={u.id}>
                      <tr style={{ borderTop: '1px solid #e5e7eb', backgroundColor: editingUser?.id === u.id ? '#eff6ff' : 'white' }}>
                        <td style={{ padding: '0.75rem 1rem', color: '#1f2937' }}>{u.first_name} {u.last_name}</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{u.email}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: '999px', fontSize: '0.875rem' }}>
                            {roleLabel(u.role)}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                          {u.last_login ? new Date(u.last_login).toLocaleString('sk-SK') : '—'}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {editingUser?.id === u.id ? (
                              <button onClick={cancelEdit} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>
                                Zrušiť
                              </button>
                            ) : (
                              <button onClick={() => startEdit(u)} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                Editovať
                              </button>
                            )}
                            {u.id !== user.id && (
                              <button onClick={() => deleteUser(u.id)} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                Vymazať
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {editingUser?.id === u.id && (
                        <tr style={{ backgroundColor: '#eff6ff', borderTop: '1px solid #bfdbfe' }}>
                          <td colSpan={5} style={{ padding: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'end' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Meno</label>
                                <input style={inputSmallStyle} value={editForm.first_name} onChange={e => setEditForm({...editForm, first_name: e.target.value})} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Priezvisko</label>
                                <input style={inputSmallStyle} value={editForm.last_name} onChange={e => setEditForm({...editForm, last_name: e.target.value})} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Email</label>
                                <input style={inputSmallStyle} type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Rola</label>
                                <select style={inputSmallStyle} value={editForm.role} onChange={e => setEditForm({...editForm, role: e.target.value})}>
                                  {/* Ak má používateľ rolu nad rámec aktuálneho počtu úrovní
                                      (firma počet znížila), ponuka ju musí obsahovať — inak by
                                      sa mu pri uložení potichu prepísala. */}
                                  {Array.from(new Set([
                                    ...levelOptions(levelCount),
                                    ...(levelFromRole(editForm.role) !== null ? [levelFromRole(editForm.role) as number] : []),
                                  ])).sort((a, b) => a - b).map(n => (
                                    <option key={n} value={roleForLevel(n)}>{levelRoleLabel(n)}</option>
                                  ))}
                                  <option value="accountant">Účtovník</option>
                                  <option value="admin">Admin</option>
                                  <option value="submitter">Zberač dokladov (iba nahráva)</option>
                                  <option value="viewer">Pozorovateľ</option>
                                </select>
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Nové heslo (voliteľné)</label>
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                  <input style={{ ...inputSmallStyle, flex: 1 }} type="text" placeholder="nezadaj = bez zmeny" value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} />
                                  <button type="button" onClick={() => setEditForm({...editForm, password: generatePassword()})}
                                    style={{ padding: '0.3rem 0.6rem', backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                    Generovať
                                  </button>
                                </div>
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Slack User ID (voliteľné)</label>
                                <input style={inputSmallStyle} placeholder="U0123456789" value={editForm.slack_user_id} onChange={e => setEditForm({...editForm, slack_user_id: e.target.value})} />
                              </div>
                            </div>
                            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Notifikácie</div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.35rem' }}>Čo dostávať</div>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginBottom: '0.3rem', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={editForm.notify_summary} onChange={e => setEditForm({...editForm, notify_summary: e.target.checked})} /> Ranný sumár (8:30)
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginBottom: '0.3rem', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={editForm.notify_new_document} onChange={e => setEditForm({...editForm, notify_new_document: e.target.checked})} /> Nový doklad na mojej úrovni
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={editForm.notify_issue} onChange={e => setEditForm({...editForm, notify_issue: e.target.checked})} /> Nespracovaný doklad / chyba
                                  </label>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.35rem' }}>Kam doručiť</div>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginBottom: '0.3rem', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={editForm.notify_via_email} onChange={e => setEditForm({...editForm, notify_via_email: e.target.checked})} /> Email
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginBottom: '0.3rem', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={editForm.notify_via_slack} onChange={e => setEditForm({...editForm, notify_via_slack: e.target.checked})} /> Slack
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginBottom: '0.4rem', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={editForm.notify_via_telegram} onChange={e => setEditForm({...editForm, notify_via_telegram: e.target.checked})} /> Telegram
                                  </label>
                                  {editForm.notify_via_telegram && (
                                    <input style={inputSmallStyle} placeholder="Telegram chat ID" value={editForm.telegram_chat_id} onChange={e => setEditForm({...editForm, telegram_chat_id: e.target.value})} />
                                  )}
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginBottom: '0.3rem', cursor: 'pointer' }}
                                    title="Push do PWA appky. Doručuje sa len na zariadenia, kde si user notifikácie sám zapol (menu vpravo hore v Schvaľovaní).">
                                    <input type="checkbox" checked={editForm.notify_via_push} onChange={e => setEditForm({...editForm, notify_via_push: e.target.checked})} /> Push (mobil)
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div style={{ marginTop: '0.75rem' }}>
                              <button onClick={saveEdit} disabled={editSaving} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: editSaving ? 0.6 : 1 }}>
                                {editSaving ? 'Ukladám...' : 'Uložiť zmeny'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PRAVIDLÁ */}
        {activeTab === 'rules' && (
          <div>
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginTop: 0, color: '#1f2937' }}>Schvaľovacie úrovne</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <label style={{ color: '#374151' }}>Počet úrovní</label>
                <select
                  style={{ ...inputStyle, width: 'auto' }}
                  value={levelCount}
                  disabled={levelCountSaving}
                  onChange={e => saveLevelCount(parseInt(e.target.value, 10))}
                >
                  {Array.from({ length: MAX_LEVEL }, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0.5rem 0 0' }}>
                Určuje, ktoré úrovne sa dajú vyberať v pravidlách a prideľovať používateľom.
                Zníženie počtu nezastaví doklady, ktoré už na vyššej úrovni čakajú — tie dobehnú.
              </p>
            </div>
            {/* Pravidlá sú per modul — objednávky majú vlastnú sadu (module='orders') */}
            {hasOrdersFeature && (
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                {([['documents', 'Došlé doklady'], ['orders', 'Objednávky']] as const).map(([m, label]) => (
                  <button key={m} onClick={() => setRulesModule(m)}
                    style={{
                      padding: '0.5rem 1.1rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
                      background: rulesModule === m ? (m === 'orders' ? '#0891b2' : '#2563eb') : 'white',
                      color: rulesModule === m ? 'white' : '#374151',
                      border: rulesModule === m ? 'none' : '1px solid #d1d5db',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginTop: 0, color: '#1f2937' }}>
                {rulesModule === 'orders' ? 'Pridať pravidlo pre objednávky' : 'Pridať pravidlo'}
              </h2>
              <form onSubmit={addRule} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Názov pravidla</label>
                  <input style={inputStyle} value={newRule.name} onChange={e => setNewRule({...newRule, name: e.target.value})} required placeholder="napr. Do 50 EUR" />
                </div>
                {rulesModule === 'documents' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Kategória (voliteľné)</label>
                    <select style={inputStyle} value={newRule.category} onChange={e => setNewRule({...newRule, category: e.target.value})}>
                      <option value="">— Všetky kategórie —</option>
                      {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Suma od (EUR)</label>
                  <input style={inputStyle} type="number" value={newRule.amountMin} onChange={e => setNewRule({...newRule, amountMin: Number(e.target.value)})} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Suma do (EUR, prázdne = bez limitu)</label>
                  <input style={inputStyle} type="number" value={newRule.amountMax} onChange={e => setNewRule({...newRule, amountMax: e.target.value})} />
                </div>
                {!newRule.autoApprove && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Požadovaná úroveň schválenia</label>
                    <LevelPicker levelCount={levelCount} value={newRule.requiredLevel} onChange={v => setNewRule({...newRule, requiredLevel: v})} />
                  </div>
                )}
                {rulesModule === 'documents' && (
                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={newRule.autoApprove}
                      onChange={e => setNewRule({...newRule, autoApprove: e.target.checked})}
                      style={{ marginTop: '0.2rem' }} />
                    <span>
                      <b>Automaticky schváliť</b> (preskočí celý schvaľovací reťazec)
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.15rem' }}>
                        Doklad ktorý sedí na toto pravidlo sa schváli rovno pri príchode.
                        Napr. DKV bločky: text „DKV" + strop sumy. Mesačná DKV faktúra strop presiahne → klasické schvaľovanie.
                      </div>
                    </span>
                  </label>
                </div>
                )}
                {newRule.autoApprove && (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Text v doklade (vendor + popis)</label>
                      <input style={inputStyle} value={newRule.textPattern}
                        onChange={e => setNewRule({...newRule, textPattern: e.target.value})} placeholder="napr. DKV" />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Dodávateľ obsahuje (voliteľné)</label>
                      <input style={inputStyle} value={newRule.vendorPattern}
                        onChange={e => setNewRule({...newRule, vendorPattern: e.target.value})} placeholder="voliteľné" />
                    </div>
                  </>
                )}
                <div style={{ gridColumn: '1 / -1' }}>
                  <button type="submit" style={{ padding: '0.75rem 2rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem' }}>
                    Pridať pravidlo
                  </button>
                </div>
              </form>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Názov</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Rozsah súm</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Kategória</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Úroveň</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Akcia</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map(r => (
                    <React.Fragment key={r.id}>
                      <tr style={{ borderTop: '1px solid #e5e7eb', backgroundColor: editingRule?.id === r.id ? '#eff6ff' : 'white' }}>
                        <td style={{ padding: '0.75rem 1rem', color: '#1f2937' }}>{r.name}</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>
                          {r.amount_min}€ — {r.amount_max ? `${r.amount_max}€` : 'bez limitu'}
                          {(r as any).auto_approve && (r as any).text_pattern && (
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>text: „{(r as any).text_pattern}"</div>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{r.category || '—'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            {(r as any).auto_approve ? (
                              <span style={{ padding: '0.2rem 0.6rem', backgroundColor: '#1e40af', color: 'white', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600 }}>⚡ Auto</span>
                            ) : (
                              <>
                                {parseChain(r.required_level).map(n => (
                                  <span key={n} style={{ padding: '0.2rem 0.6rem', backgroundColor: levelColor(n), color: 'white', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600 }}>L{n}</span>
                                ))}
                              </>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {editingRule?.id === r.id ? (
                              <button onClick={cancelEditRule} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>
                                Zrušiť
                              </button>
                            ) : (
                              <button onClick={() => startEditRule(r)} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                Editovať
                              </button>
                            )}
                            <button onClick={() => deleteRule(r.id)} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                              Vymazať
                            </button>
                          </div>
                        </td>
                      </tr>
                      {editingRule?.id === r.id && (
                        <tr style={{ backgroundColor: '#eff6ff', borderTop: '1px solid #bfdbfe' }}>
                          <td colSpan={5} style={{ padding: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.75rem', alignItems: 'end' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Názov</label>
                                <input style={inputSmallStyle} value={editRuleForm.name} onChange={e => setEditRuleForm({...editRuleForm, name: e.target.value})} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Suma od (€)</label>
                                <input style={inputSmallStyle} type="number" value={editRuleForm.amountMin} onChange={e => setEditRuleForm({...editRuleForm, amountMin: Number(e.target.value)})} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Suma do (€, prázdne = bez limitu)</label>
                                <input style={inputSmallStyle} type="number" value={editRuleForm.amountMax} onChange={e => setEditRuleForm({...editRuleForm, amountMax: e.target.value})} />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Kategória</label>
                                <select style={inputSmallStyle} value={editRuleForm.category} onChange={e => setEditRuleForm({...editRuleForm, category: e.target.value})}>
                                  <option value="">— Všetky kategórie —</option>
                                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                              </div>
                              {!editRuleForm.autoApprove && (
                                <div>
                                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Úroveň</label>
                                  <LevelPicker levelCount={levelCount} value={editRuleForm.requiredLevel} onChange={v => setEditRuleForm({...editRuleForm, requiredLevel: v})} />
                                </div>
                              )}
                            </div>
                            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                                <input type="checkbox" checked={editRuleForm.autoApprove}
                                  onChange={e => setEditRuleForm({...editRuleForm, autoApprove: e.target.checked})} />
                                <b>Automaticky schváliť</b>
                              </label>
                              {editRuleForm.autoApprove && (
                                <>
                                  <div>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Text v doklade</label>
                                    <input style={inputSmallStyle} value={editRuleForm.textPattern}
                                      onChange={e => setEditRuleForm({...editRuleForm, textPattern: e.target.value})} placeholder="napr. DKV" />
                                  </div>
                                  <div>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.8rem' }}>Dodávateľ obsahuje</label>
                                    <input style={inputSmallStyle} value={editRuleForm.vendorPattern}
                                      onChange={e => setEditRuleForm({...editRuleForm, vendorPattern: e.target.value})} placeholder="voliteľné" />
                                  </div>
                                </>
                              )}
                            </div>
                            <div style={{ marginTop: '0.75rem' }}>
                              <button onClick={saveEditRule} disabled={editRuleSaving} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: editRuleSaving ? 0.6 : 1 }}>
                                {editRuleSaving ? 'Ukladám...' : 'Uložiť zmeny'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* KATEGÓRIE */}
        {activeTab === 'categories' && (
          <div>
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginTop: 0, color: '#1f2937' }}>Kategórie dokumentov</h2>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Doklad sa pri OCR spracovaní automaticky zaradí do jednej z týchto kategórií.
                Kľúčové slová sú nápoveda — ak sa na faktúre objavia (napr. v názvoch položiek), doklad sa zaradí do danej kategórie.
              </p>
              <form onSubmit={addCategory} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Názov kategórie</label>
                  <input
                    style={inputStyle}
                    value={newCategory}
                    onChange={e => setNewCategory(e.target.value)}
                    placeholder="napr. Vozidlá, IT, Kancelárske potreby, Ostatné"
                    required
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151' }}>Kľúčové slová (voliteľné)</label>
                  <input
                    style={inputStyle}
                    value={newCategoryKeywords}
                    onChange={e => setNewCategoryKeywords(e.target.value)}
                    placeholder="typické slová z položiek, napr. hlava, TIG, brzdy, olej"
                  />
                </div>
                <button
                  type="submit"
                  disabled={categoryLoading}
                  style={{ padding: '0.75rem 1.5rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem', opacity: categoryLoading ? 0.6 : 1 }}
                >
                  Pridať
                </button>
              </form>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              {categories.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                  Žiadne kategórie — pridaj prvú kategóriu vyššie
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Názov</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Kľúčové slová</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Vytvorená</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500 }}>Akcia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map(cat => (
                      <tr key={cat.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {editingCategory === cat.id ? (
                            <input
                              style={{ ...inputSmallStyle, width: '200px' }}
                              value={editCategoryName}
                              onChange={e => setEditCategoryName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveEditCategory(cat.id); if (e.key === 'Escape') setEditingCategory(null); }}
                              autoFocus
                            />
                          ) : (
                            <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#eff6ff', color: '#2563eb', borderRadius: '999px', fontSize: '0.875rem', fontWeight: 500 }}>
                              {cat.name}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                          {editingCategory === cat.id ? (
                            <input
                              style={{ ...inputSmallStyle, width: '280px' }}
                              value={editCategoryKeywords}
                              onChange={e => setEditCategoryKeywords(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveEditCategory(cat.id); if (e.key === 'Escape') setEditingCategory(null); }}
                              placeholder="hlava, TIG, brzdy, olej"
                            />
                          ) : (
                            cat.keywords || '—'
                          )}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                          {new Date(cat.created_at).toLocaleDateString('sk-SK')}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {editingCategory === cat.id ? (
                              <>
                                <button onClick={() => saveEditCategory(cat.id)} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Uložiť</button>
                                <button onClick={() => setEditingCategory(null)} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Zrušiť</button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => { setEditingCategory(cat.id); setEditCategoryName(cat.name); setEditCategoryKeywords(cat.keywords || ''); }} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Editovať</button>
                                <button onClick={() => deleteCategory(cat.id)} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Vymazať</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ARCHÍV */}
        {activeTab === 'archive' && (
          <div>
            <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>Archív faktúr</h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Stiahnutie všetkých faktúr za ukončené mesiace vo formáte ZIP. Rozhodujúci je dátum faktúry.
            </p>
            {archiveLoading ? (
              <p style={{ color: '#6b7280' }}>Načítavam...</p>
            ) : archiveMonths.length === 0 ? (
              <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', textAlign: 'center', color: '#9ca3af' }}>
                Žiadne uzavreté mesiace
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {archiveMonths.map(m => (
                  <div key={m.month} style={{ backgroundColor: 'white', padding: '1rem 1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#1f2937', textTransform: 'capitalize' }}>{formatMonth(m.month)}</span>
                      <span style={{ marginLeft: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>{m.total} dokladov</span>
                    </div>
                    <button
                      onClick={() => downloadArchive(m.month)}
                      disabled={downloadingMonth === m.month}
                      style={{ padding: '0.5rem 1.25rem', backgroundColor: downloadingMonth === m.month ? '#9ca3af' : '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: downloadingMonth === m.month ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
                      {downloadingMonth === m.month ? 'Sťahujem...' : '⬇ Stiahnuť ZIP'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PODPIS — nastavenie firmy naprieč modulmi */}
        {activeTab === 'bank' && (
          <div>
            <h2 style={{ marginBottom: '0.75rem', color: '#1f2937' }}>Napojenie banky</h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem', maxWidth: 640 }}>
              Pripojte firemný účet cez PSD2 — systém potom sám sťahuje pohyby (4× denne)
              a páruje prijaté platby na vystavené faktúry. Súhlas banke platí ~90–180 dní,
              po vypršaní vás vyzveme na obnovenie.
            </p>
            <div style={{ backgroundColor: 'white', padding: '1rem 1.25rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: 760 }}>
              {bankMsg && <div style={{ fontSize: 12.5, color: '#374151', marginBottom: 8 }}>{bankMsg}</div>}
              <BankConnections notify={setBankMsg} onSynced={() => {}} />
            </div>
          </div>
        )}

        {activeTab === 'finance' && hasFinanceFeature && <FinanceSettings />}

        {activeTab === 'signature' && (
          <div>
            <h2 style={{ marginBottom: '0.75rem', color: '#1f2937' }}>Podpis firmy</h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem', maxWidth: 640 }}>
              Podpíšte sa raz — prstom na mobile či tablete, stylusom alebo myšou. Podpis sa uloží
              a bude sa automaticky tlačiť na vystavené faktúry. Nemusíte ho už vkladať do každého
              dokladu ručne.
            </p>

            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: 640 }}>
              {signatureUrl ? (
                <>
                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                    Uložený podpis
                  </div>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '1rem', display: 'flex', justifyContent: 'center', background: '#fff' }}>
                    <img src={signatureUrl} alt="Podpis firmy" style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }} />
                  </div>
                </>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '0.9rem', textAlign: 'center', padding: '1.5rem 0' }}>
                  Zatiaľ nie je nastavený žiadny podpis.
                </div>
              )}
              <button onClick={() => setShowSignature(true)}
                style={{ marginTop: '1rem', padding: '0.6rem 1.2rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                {signatureUrl ? 'Zmeniť podpis' : 'Podpísať sa'}
              </button>
            </div>
          </div>
        )}

      </div>

      {showSignature && (
        <SignaturePad
          hasExisting={!!signatureUrl}
          onClose={() => setShowSignature(false)}
          onSaved={() => { setShowSignature(false); loadSignature(); }}
        />
      )}
    </div>
  );
}